import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  createZadInitialStock,
  createZadStockAdjustment,
  fetchZadProductStock,
  fetchZadStock,
  fetchZadProductRecentMovements,
  fetchZadStockMovementById,
  fetchZadStockMovements,
  fetchZadStockProductOptions,
} from './service'
import {
  FetchZadProductStockResponse,
  FetchZadStockMovementsResponse,
  FetchZadStockResponse,
  ZadStockState,
} from './types'

const initialState: ZadStockState = {
  items: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  error: null,
  errorCode: null,
  errorStatus: null,
  selectProducts: [],
  movements: [],
  movementsTotal: 0,
  movementsTotalPages: 0,
  movementsLoading: false,
  movementsInitialized: false,
  movementsError: null,
  movementsErrorCode: null,
  movementsErrorStatus: null,
  productStock: null,
  productMovements: [],
  productMovementsLoading: false,
  selectedMovement: null,
  detailLoading: false,
  actionLoading: false,
}

function applyOverviewRejected(state: ZadStockState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function applyMovementsRejected(state: ZadStockState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.movementsError = payload.error || 'Request failed'
  state.movementsErrorCode = payload.errorCode || null
  state.movementsErrorStatus = payload.status || null
}

function clearOverviewError(state: ZadStockState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function clearMovementsError(state: ZadStockState) {
  state.movementsError = null
  state.movementsErrorCode = null
  state.movementsErrorStatus = null
}

const zadStockSlice = createSlice({
  name: 'zadStock',
  initialState,
  reducers: {
    clearZadStockError: (state: ZadStockState) => {
      clearOverviewError(state)
      clearMovementsError(state)
    },
    clearZadStockDetail: (state: ZadStockState) => {
      state.productStock = null
      state.selectedMovement = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadStock.pending, (state: ZadStockState) => {
      state.listLoading = true
      clearOverviewError(state)
    })
    builder.addCase(fetchZadStock.rejected, (state: ZadStockState, action: PayloadAction<any>) => {
      applyOverviewRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadStock.fulfilled,
      (state: ZadStockState, action: PayloadAction<FetchZadStockResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearOverviewError(state)
        state.items = action.payload.items
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadStockProductOptions.fulfilled, (state: ZadStockState, action) => {
      state.selectProducts = action.payload.products
    })

    builder.addCase(fetchZadStockMovements.pending, (state: ZadStockState) => {
      state.movementsLoading = true
      clearMovementsError(state)
    })
    builder.addCase(
      fetchZadStockMovements.rejected,
      (state: ZadStockState, action: PayloadAction<any>) => {
        applyMovementsRejected(state, action)
        state.movementsLoading = false
        state.movementsInitialized = true
      }
    )
    builder.addCase(
      fetchZadStockMovements.fulfilled,
      (state: ZadStockState, action: PayloadAction<FetchZadStockMovementsResponse>) => {
        state.movementsLoading = false
        state.movementsInitialized = true
        clearMovementsError(state)
        state.movements = action.payload.movements
        state.movementsTotal = action.payload.total
        state.movementsTotalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadProductRecentMovements.pending, (state: ZadStockState) => {
      state.productMovementsLoading = true
    })
    builder.addCase(fetchZadProductRecentMovements.rejected, (state: ZadStockState) => {
      state.productMovementsLoading = false
    })
    builder.addCase(fetchZadProductRecentMovements.fulfilled, (state: ZadStockState, action) => {
      state.productMovementsLoading = false
      state.productMovements = action.payload.movements
    })

    builder.addCase(fetchZadProductStock.pending, (state: ZadStockState) => {
      state.detailLoading = true
    })
    builder.addCase(fetchZadProductStock.rejected, (state: ZadStockState) => {
      state.detailLoading = false
    })
    builder.addCase(
      fetchZadProductStock.fulfilled,
      (state: ZadStockState, action: PayloadAction<FetchZadProductStockResponse>) => {
        state.detailLoading = false
        state.productStock = {
          product: action.payload.product,
          currentStock: action.payload.currentStock,
        }
      }
    )

    builder.addCase(fetchZadStockMovementById.pending, (state: ZadStockState) => {
      state.detailLoading = true
    })
    builder.addCase(fetchZadStockMovementById.rejected, (state: ZadStockState) => {
      state.detailLoading = false
    })
    builder.addCase(fetchZadStockMovementById.fulfilled, (state: ZadStockState, action) => {
      state.detailLoading = false
      state.selectedMovement = action.payload.movement
    })

    builder.addCase(createZadInitialStock.pending, (state: ZadStockState) => {
      state.actionLoading = true
    })
    builder.addCase(createZadInitialStock.rejected, (state: ZadStockState) => {
      state.actionLoading = false
    })
    builder.addCase(createZadInitialStock.fulfilled, (state: ZadStockState) => {
      state.actionLoading = false
    })

    builder.addCase(createZadStockAdjustment.pending, (state: ZadStockState) => {
      state.actionLoading = true
    })
    builder.addCase(createZadStockAdjustment.rejected, (state: ZadStockState) => {
      state.actionLoading = false
    })
    builder.addCase(createZadStockAdjustment.fulfilled, (state: ZadStockState) => {
      state.actionLoading = false
    })
  },
})

export default zadStockSlice.reducer
export const actions = zadStockSlice.actions
export {initialState as zadStockInitialState}
